import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";

const fmtDt = (iso) => (iso ? new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-");

export default function FinanceReceivablesReport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === "OWNER";
  const [status, setStatus] = useState("OPEN");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [expBusy, setExpBusy] = useState("");

  const exportPiutang = async (fmt) => {
    setExpBusy(fmt); setError("");
    try {
      const res = await api.get(`/marketplace/receivables/export?status=${encodeURIComponent(status)}&search=${encodeURIComponent(q.trim())}&fmt=${fmt}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `piutang_${status}_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch { setError("Export piutang gagal."); } finally { setExpBusy(""); }
  };

  useEffect(() => {
    if (!isOwner) return;
    setLoading(true); setError("");
    api.get(`/reports/receivables?status=${status}`)
      .then(({ data }) => setRows(Array.isArray(data?.rows) ? data.rows : []))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [isOwner, status]);

  if (user && !isOwner) {
    return (<div className="app-shell"><AppTopBar active="reports" /><main className="content"><div className="alert-error">Akses ditolak.</div></main></div>);
  }

  const term = q.trim().toLowerCase();
  const shown = term ? rows.filter((r) =>
    (r.order_id || "").toLowerCase().includes(term) ||
    (r.booking_id || "").toLowerCase().includes(term)) : rows;
  const total = shown.reduce((s, r) => s + (parseFloat(r.frozen_gross || 0) || 0), 0);

  return (
    <div className="app-shell">
      <AppTopBar active="reports" />
      <main className="content">
        <div style={{ width: "100%", maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18 }}>
            <div>
              <button className="btn-ghost inline" onClick={() => navigate("/reports")} style={{ marginBottom: 10 }}>← Health Check & Audit</button>
              <h1 style={{ margin: "0 0 6px", fontSize: 26 }}>Laporan Piutang</h1>
              <p className="hint" style={{ margin: 0 }}>Read-only. Sumber canonical Marketplace Receivable.</p>
            </div>
            <span className="hint">OWNER · FINANCE</span>
          </div>

          {error && <div className="alert-error">{error}</div>}

          <section className="panel" style={{ marginTop: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div><label className="field-label">Status</label><select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}><option value="OPEN">OPEN</option><option value="CLOSED">CLOSED</option><option value="ALL">SEMUA</option></select></div>
                <div style={{ width: 300, maxWidth: "100%" }}><label className="field-label">Cari</label><input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari Order ID / Invoice..." data-testid="piutang-search" /></div>
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ textAlign: "right" }}><div className="meta-label">TOTAL PIUTANG TERLIHAT</div><div className="mono" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{formatIDR(total)}</div></div>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                  <button className="btn-mini on" disabled={!!expBusy} onClick={() => exportPiutang("xlsx")} data-testid="export-piutang-xlsx">{expBusy === "xlsx" ? "Menyiapkan…" : "Export Piutang (.xlsx)"}</button>
                  <button className="btn-mini" disabled={!!expBusy} onClick={() => exportPiutang("csv")} data-testid="export-piutang-csv">.csv</button>
                </div>
              </div>
            </div>

            <div className="table-scroll"><table className="data-table" data-testid="receivables-report-table">
              <thead><tr><th>Order ID</th><th>Booking</th><th>Marketplace</th><th>Toko</th><th>FINAL CLEAR</th><th>Frozen Gross</th><th>Status</th><th>Close Reason</th><th>Umur</th><th>Return ID</th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan={10} className="empty-cell">Memuat...</td></tr>}
                {!loading && shown.length === 0 && <tr><td colSpan={10} className="empty-cell">Tidak ada data piutang.</td></tr>}
                {!loading && shown.map((r) => <tr key={r.id}><td className="mono">{r.order_id || "-"}</td><td className="mono">{r.booking_id || "-"}</td><td>{r.marketplace || "-"}</td><td>{r.shop || "-"}</td><td>{fmtDt(r.final_clear_date)}</td><td className="mono">{formatIDR(r.frozen_gross)}</td><td><span className={`status-pill ${r.status === "OPEN" ? "on" : "off"}`}>{r.status}</span></td><td>{r.close_reason || "-"}</td><td className="mono">{r.age_days ?? "-"}</td><td className="mono">{r.return_id || "-"}</td></tr>)}
              </tbody>
            </table></div>
          </section>
        </div>
      </main>
    </div>
  );
}
